import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppLayout } from '../AppShell/AppLayoutContext';
import {
  CONTROL_BRIDGE_TAB_SURFACES,
  controlBridgeTabIsOffered,
  deriveControlBridgeViewModel,
  isHeldControlBridgeSurface,
  type ControlBridgeWorkSurfaceKey,
} from './deriveViewModel';
import type { KryspaceRenderManifest, RenderManifestQueueItem } from '../../types/kryspaceRenderManifest';
import type {
  ControlBridgeStructureNode,
  KryspaceControlBridgeStructure,
} from '../../types/kryspaceControlBridgeStructure';
import type { ResourceFilter, ScopeContext } from './filters';
import { passesFilter } from './filters';
import { buildScopeContext, resolveScopeLabel } from './structureScope';
import { buildResourceOptions, resourceFilterLabel } from './queueHelpers';
import { cbDisplayCompanyTitle } from './cbChromeHelpers';
import { useControlBridgeSurfaceChatContext } from './useControlBridgeSurfaceChatContext';
import type { PersonalOverlayMode } from './personalLayer';
import type { PersonalLayerUserContext } from './personalLayer/buildPersonalLayerViewModels';
import {
  expandSidebarForScope,
  initialSidebarCollapseState,
  nodeScopeKey,
  readSidebarCollapsedPreference,
  toggleCollapsedMap,
  writeSidebarCollapsedPreference,
} from './structureSidebarUi';
import { filterDriveLaneChildNodes } from './deriveSubdomainFlow';
import { scopeIdsEqual } from '../../utils/scopeIdPlane';
import { deriveAttentionRouting } from './attentionRouting';
import {
  ControlBridgeDetailPanel,
  type ControlBridgeDetailSelection,
} from './ControlBridgeDetailPanel';
import { ControlBridgeEntityDetailOverlay } from './detail/ControlBridgeEntityDetailOverlay';
import type { ControlBridgeEntityDetailTarget } from './detail/entityDetailTargets';
import type { ControlBridgeQueueItem } from './deriveViewModel';
import {
  DiagnoseSurface,
  DriveSurface,
  ExoPanelSurface,
  GuidanceSurface,
  HeldSurfaceUnavailable,
  PersonalOrganizerSurface,
} from './surfaces/ControlBridgeSurfaces';
import { MissionControlPersonalLayer } from './missionControl/MissionControlPersonalLayer';
import { PersonalLayerOverlayToggle } from './personalLayer';
import {
  deriveProvisionalRoleLens,
  structureNodesForProvisionalLens,
  surfaceOwnedByLens,
  type RoleLens,
} from './derivation/roleLens';
import { resolveClickTarget } from './derivation/clickTarget';
import {
  buildControlBridgeModelOptions,
  modelOptionClientLabel,
  modelOptionClientMeta,
  type ControlBridgeModelOption,
} from './controlBridgeModelOptions';
import { resolvePersonalModelSlugFromUsername } from '../../utils/resolvePersonalModelSlug';
import {
  parseCrossDomainAttention,
  previewCrossDomainQueue,
  normalizeMode,
  type CrossDomainMode,
} from './hub/crossDomainAttention';
import { CrossDomainAttentionStrip } from './hub/CrossDomainAttentionStrip';
import { UnifiedHubChrome, type UnifiedHubCompanyLink } from './hub/UnifiedHubChrome';
import {
  CONTROL_BRIDGE_PENDING_NAV_EVENT,
  clearControlBridgePendingNav,
  peekControlBridgePendingNav,
} from '../../utils/controlBridgePendingNavigation';
import {
  readControlBridgePrimaryWorkspace,
  rememberPrimaryWhenLeavingWorkspace,
  resolveControlBridgePrimaryWorkspace,
  writeControlBridgePrimaryWorkspace,
} from '../../utils/controlBridgePrimaryWorkspace';
import { resolveUserResourceProfileRef } from './resolveUserResourceProfileRef';
import type { ExoWorkDeeplink } from './exoPanel/exoPanelWorkDeeplink';
import { findQueueItemById, queueItemsFromViewModel } from './detail/findQueueItemById';
import { canonicalizeMcDomainScope } from './detail/resolveArtifactInstanceId';
import { ReportIssueDialog } from './reportIssue/ReportIssueDialog';
import styles from './ControlBridge.module.css';

export interface UnifiedHubShellConfig {
  personalSlug: string;
  personalLabel: string;
  companyLinks: UnifiedHubCompanyLink[];
}

export interface ControlBridgeShellProps {
  manifest: KryspaceRenderManifest;
  structure: KryspaceControlBridgeStructure;
  devMock?: boolean;
  workspaceSlug: string;
  /**
   * Company home Kryspace slug (selected company). When the CB is showing a
   * linked/engineering submodel, this is the workspace to return to.
   */
  preferredPrimarySlug?: string | null;
  preferredPrimaryTitle?: string | null;
  initialSurface?: ControlBridgeWorkSurfaceKey | null;
  personalLayerUser?: PersonalLayerUserContext;
  onManifestRefresh?: () => void | Promise<void>;
  /** Quiet attention-manifest refetch with Drive ``drive_filter_scope`` / ``drive_period``. */
  onDriveRailChange?: (
    scope: string,
    opts?: { drivePeriod?: string }
  ) => void | Promise<void>;
  unifiedHub?: UnifiedHubShellConfig;
}

function sidebarMonogram(title: string): string {
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]![0] ?? ''}${words[1]![0] ?? ''}`.toUpperCase();
  }
  return title.slice(0, 2).toUpperCase();
}

function SidebarStructureIcon() {
  return (
    <svg
      className={styles.sidebarRailIconSvg}
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M10 4h4a1 1 0 0 1 1 1v2h3a1 1 0 0 1 1 1v3h2a1 1 0 0 1 1 1v7h-4v-4H8v4H4v-7h2v-3a1 1 0 0 1 1-1h3V5a1 1 0 0 1 1-1Zm4 2h-4v1h4V6ZM7 11v2h10v-2H7Zm-2 4v3h3v-2h8v2h3v-3H5Z"
      />
    </svg>
  );
}

function StructureDomainNode({
  node,
  scope,
  collapsedDomains,
  onToggleDomain,
  onScopeChange,
  eligibleWorkItems = [],
}: {
  node: ControlBridgeStructureNode;
  scope: string;
  collapsedDomains: Record<string, boolean>;
  onToggleDomain: (nodeKey: string) => void;
  onScopeChange: (scopeKey: string) => void;
  eligibleWorkItems?: ControlBridgeQueueItem[];
}) {
  const scopeKey = nodeScopeKey(node);
  // Same scaffolding filter as Drive/Diagnose (drop empty ai-* subdomain lanes; keep ca-*).
  const children = filterDriveLaneChildNodes(node.children ?? [], [], eligibleWorkItems);
  const hasChildren = children.length > 0;
  const childrenHidden = Boolean(collapsedDomains[node.key]);

  return (
    <>
      <div className={styles.sidebarTreeRow}>
        {hasChildren ? (
          <button
            type="button"
            className={styles.sidebarTreeChevronBtn}
            aria-expanded={!childrenHidden}
            aria-label={`${childrenHidden ? 'Expand' : 'Collapse'} ${node.label}`}
            onClick={() => onToggleDomain(node.key)}
          >
            <span
              className={`${styles.sidebarTreeChevron} ${childrenHidden ? '' : styles.sidebarTreeChevronOpen}`}
              aria-hidden
            />
          </button>
        ) : (
          <span className={styles.sidebarTreeChevronSpacer} aria-hidden />
        )}
        <button
          type="button"
          className={`${styles.treeButton} ${scopeIdsEqual(scope, scopeKey) ? styles.treeButtonActive : ''}`}
          onClick={() => onScopeChange(scopeKey)}
          title={node.crosses ?? node.desc}
        >
          {node.label}
        </button>
      </div>
      {hasChildren && !childrenHidden
        ? children.map((child) => {
            const childScope = nodeScopeKey(child);
            return (
              <button
                key={child.key}
                type="button"
                className={`${styles.treeButton} ${styles.treeButtonNested} ${scopeIdsEqual(scope, childScope) ? styles.treeButtonActive : ''}`}
                onClick={() => onScopeChange(childScope)}
                title={child.desc}
              >
                {child.label}
              </button>
            );
          })
        : null}
    </>
  );
}

const COMPANY_MODELS_GROUP_ID = '__company_models__';

function StructureSidebar({
  structure,
  companyTitle,
  scope,
  surface,
  onScopeChange,
  modelOptions,
  currentWorkspaceSlug,
  onSelectModel,
  sidebarCollapsed,
  onSidebarCollapsedChange,
  /** Queue used only to scaffold domain/subdomain lanes (not rendered as work lists). */
  eligibleWorkItems = [],
}: {
  structure: KryspaceControlBridgeStructure;
  companyTitle: string;
  scope: string;
  surface: ControlBridgeWorkSurfaceKey | null;
  onScopeChange: (scopeKey: string) => void;
  modelOptions: ControlBridgeModelOption[];
  currentWorkspaceSlug: string;
  onSelectModel: (slug: string) => void;
  sidebarCollapsed: boolean;
  onSidebarCollapsedChange: (collapsed: boolean) => void;
  eligibleWorkItems?: ControlBridgeQueueItem[];
}) {
  const focusSurface = surface === null;
  const initialCollapse = useMemo(
    () => initialSidebarCollapseState(structure, { focusSurface }),
    [structure, focusSurface]
  );
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(
    () => ({
      ...initialCollapse.groups,
      [COMPANY_MODELS_GROUP_ID]: false,
    })
  );
  const [collapsedDomains, setCollapsedDomains] = useState<Record<string, boolean>>(
    () => initialCollapse.domains
  );

  useEffect(() => {
    const base = initialSidebarCollapseState(structure, { focusSurface });
    const next = expandSidebarForScope(structure, scope, base.groups, base.domains);
    setCollapsedGroups((prev) => ({
      ...next.groups,
      [COMPANY_MODELS_GROUP_ID]: prev[COMPANY_MODELS_GROUP_ID] ?? false,
    }));
    setCollapsedDomains(next.domains);
  }, [structure, scope, focusSurface]);

  const options = modelOptions ?? [];
  const showModelPicker = options.length > 1;
  const modelsExpanded = !collapsedGroups[COMPANY_MODELS_GROUP_ID];

  return (
    <>
      <div className={`${styles.sidebarIdentity} ${sidebarCollapsed ? styles.sidebarIdentityRail : ''}`}>
        {sidebarCollapsed ? (
          <div className={styles.sidebarRailStack}>
            <div
              className={styles.sidebarRailIconWrap}
              title={companyTitle}
              data-testid="sidebar-rail-icon"
            >
              <SidebarStructureIcon />
              <span className={styles.sidebarRailMonogram}>{sidebarMonogram(companyTitle)}</span>
            </div>
            <button
              type="button"
              className={styles.sidebarRailToggle}
              aria-expanded={false}
              aria-label="Expand sidebar"
              title="Expand sidebar"
              onClick={() => onSidebarCollapsedChange(false)}
            >
              »
            </button>
          </div>
        ) : (
          <div className={styles.sidebarIdentityRow}>
            <div className={styles.sidebarIdentityText}>
              <div className={styles.companyName}>{companyTitle}</div>
              <div className={styles.bridgeType}>Structure</div>
            </div>
            <button
              type="button"
              className={styles.sidebarRailToggle}
              aria-expanded
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              onClick={() => onSidebarCollapsedChange(true)}
            >
              «
            </button>
          </div>
        )}
      </div>
      {!sidebarCollapsed && showModelPicker ? (
        <div className={styles.sidebarGroup} aria-label="Models">
          <button
            type="button"
            className={styles.sidebarGroupHeader}
            aria-expanded={modelsExpanded}
            onClick={() =>
              setCollapsedGroups((prev) => toggleCollapsedMap(prev, COMPANY_MODELS_GROUP_ID))
            }
          >
            <span
              className={`${styles.sidebarTreeChevron} ${modelsExpanded ? styles.sidebarTreeChevronOpen : ''}`}
              aria-hidden
            />
            <span>Models</span>
            <span className={styles.sidebarGroupCount}>{options.length}</span>
          </button>
          {modelsExpanded ? (
            <div className={styles.workspaceLinks}>
              {options.map((option) => {
                const label = modelOptionClientLabel(option);
                const meta = modelOptionClientMeta(option);
                return (
                <button
                  key={option.slug}
                  type="button"
                  className={`${styles.modelOptionButton} ${
                    option.slug === currentWorkspaceSlug ? styles.modelOptionButtonActive : ''
                  }`}
                  aria-current={option.slug === currentWorkspaceSlug ? 'true' : undefined}
                  title={label}
                  onClick={() => onSelectModel(option.slug)}
                >
                  <span className={styles.modelOptionTitle}>{label}</span>
                  {meta ? (
                    <span className={styles.modelOptionMeta}>{meta}</span>
                  ) : null}
                </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
      {!sidebarCollapsed ? (
        <div className={styles.sidebarScroll}>
          <button
            type="button"
            className={`${styles.treeButton} ${scope === 'company' ? styles.treeButtonActive : ''}`}
            onClick={() => onScopeChange('company')}
          >
            Company
          </button>

          {structure.groups.map((group) =>
            group.nodes.length > 0 ? (
              <div key={group.id} className={styles.sidebarGroup}>
                <button
                  type="button"
                  className={styles.sidebarGroupHeader}
                  aria-expanded={!collapsedGroups[group.id]}
                  onClick={() => setCollapsedGroups((prev) => toggleCollapsedMap(prev, group.id))}
                >
                  <span
                    className={`${styles.sidebarTreeChevron} ${collapsedGroups[group.id] ? '' : styles.sidebarTreeChevronOpen}`}
                    aria-hidden
                  />
                  <span>{group.label}</span>
                </button>
                {!collapsedGroups[group.id]
                  ? group.nodes.map((node) => (
                      <StructureDomainNode
                        key={node.key}
                        node={node}
                        scope={scope}
                        collapsedDomains={collapsedDomains}
                        onToggleDomain={(key) =>
                          setCollapsedDomains((prev) => toggleCollapsedMap(prev, key))
                        }
                        onScopeChange={onScopeChange}
                        eligibleWorkItems={eligibleWorkItems}
                      />
                    ))
                  : null}
              </div>
            ) : null
          )}
        </div>
      ) : null}
    </>
  );
}

export const ControlBridgeShell: React.FC<ControlBridgeShellProps> = ({
  manifest,
  structure,
  devMock,
  workspaceSlug,
  preferredPrimarySlug = null,
  preferredPrimaryTitle = null,
  initialSurface = null,
  personalLayerUser,
  onManifestRefresh,
  onDriveRailChange,
  unifiedHub,
}) => {
  const navigate = useNavigate();
  const { expandChatPanel } = useAppLayout();
  const [sessionPrimarySlug, setSessionPrimarySlug] = useState<string | null>(() =>
    readControlBridgePrimaryWorkspace()
  );

  useEffect(() => {
    const preferred = preferredPrimarySlug?.trim() || null;
    if (preferred && preferred !== workspaceSlug.trim()) {
      writeControlBridgePrimaryWorkspace(preferred);
      setSessionPrimarySlug(preferred);
      return;
    }
    if (preferred && preferred === workspaceSlug.trim()) {
      writeControlBridgePrimaryWorkspace(null);
      setSessionPrimarySlug(null);
      return;
    }
    setSessionPrimarySlug(readControlBridgePrimaryWorkspace());
  }, [preferredPrimarySlug, workspaceSlug]);

  const primaryReturnSlug = useMemo(
    () =>
      resolveControlBridgePrimaryWorkspace({
        currentWorkspaceSlug: workspaceSlug,
        companyKryspaceSlug: preferredPrimarySlug,
        sessionPrimarySlug,
      }),
    [workspaceSlug, preferredPrimarySlug, sessionPrimarySlug]
  );

  const vm = useMemo(() => deriveControlBridgeViewModel(manifest), [manifest]);
  const crossDomainAttention = useMemo(
    () => (unifiedHub ? parseCrossDomainAttention(manifest) : null),
    [manifest, unifiedHub]
  );
  const [crossDomainMode, setCrossDomainMode] = useState<CrossDomainMode>(() =>
    normalizeMode(crossDomainAttention?.modeDefault)
  );
  useEffect(() => {
    if (crossDomainAttention) {
      setCrossDomainMode(normalizeMode(crossDomainAttention.modeDefault));
    }
  }, [crossDomainAttention?.modeDefault, manifest.generatedAt]);
  const crossDomainPreview = useMemo(
    () =>
      crossDomainAttention
        ? previewCrossDomainQueue(crossDomainAttention.queue, crossDomainMode)
        : [],
    [crossDomainAttention, crossDomainMode]
  );
  const modelOptions = useMemo(
    () =>
      buildControlBridgeModelOptions({
        workspaceSlug,
        companyTitle: structure.companyTitle || vm.companyTitle,
        manifest,
        structure,
        mcEngineeringWorkspaces: vm.mcEngineeringWorkspaces,
        primaryWorkspaceSlug: primaryReturnSlug,
        primaryWorkspaceTitle: preferredPrimaryTitle,
        personalWorkspaceSlug: resolvePersonalModelSlugFromUsername(personalLayerUser?.username),
        personalWorkspaceTitle: personalLayerUser?.label || 'Personal model',
      }),
    [
      workspaceSlug,
      structure,
      manifest,
      vm.companyTitle,
      vm.mcEngineeringWorkspaces,
      primaryReturnSlug,
      preferredPrimaryTitle,
      personalLayerUser?.username,
      personalLayerUser?.label,
    ]
  );
  const scopeContext: ScopeContext = useMemo(() => buildScopeContext(structure), [structure]);
  const roleLens: RoleLens | null = useMemo(() => {
    if (vm.roleLens) return vm.roleLens;
    const domains = structureNodesForProvisionalLens(structure);
    if (!domains.length) return null;
    // Operating provisional GM at subdomain grain until daily-sketch intake (FE-ROLE-2).
    return deriveProvisionalRoleLens('gm', domains);
  }, [vm.roleLens, structure]);
  const resourceOptions = useMemo(
    () => buildResourceOptions([...vm.eligibleWork, ...vm.openIssues]),
    [vm.eligibleWork, vm.openIssues]
  );
  const [surface, setSurface] = useState<ControlBridgeWorkSurfaceKey | null>(initialSurface);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [issuesFocusItemId, setIssuesFocusItemId] = useState<string | null>(null);
  const [issuesRefreshToken, setIssuesRefreshToken] = useState(0);
  const [diagnoseIssuesListOpen, setDiagnoseIssuesListOpen] = useState(false);
  const mcFocus = surface === null;
  const [scope, setScopeState] = useState<string>('company');
  const scopeBeforeMcExpandRef = useRef<string | null>(null);
  const handleScopeChange = (next: string) => {
    scopeBeforeMcExpandRef.current = null;
    setScopeState(canonicalizeMcDomainScope(next, structure));
  };
  const driveRailSentRef = useRef<string | null>(null);
  const drivePeriodRef = useRef<string | undefined>(undefined);
  const handleDrivePeriodChange = useCallback(
    (periodCode: string) => {
      const next = (periodCode || '').trim() || undefined;
      if (next === drivePeriodRef.current) return;
      drivePeriodRef.current = next;
      if (surface === 'drive') {
        void onDriveRailChange?.(scope, { drivePeriod: next });
      }
    },
    [surface, scope, onDriveRailChange]
  );
  useEffect(() => {
    if (surface !== 'drive') return;
    const rail = !scope || scope === 'company' ? '' : scope;
    if (!rail && !driveRailSentRef.current && !drivePeriodRef.current) return;
    driveRailSentRef.current = rail || null;
    void onDriveRailChange?.(scope, { drivePeriod: drivePeriodRef.current });
  }, [surface, scope, onDriveRailChange]);
  const handleMcDomainScope = (filterScope: string | null) => {
    if (filterScope) {
      if (scopeBeforeMcExpandRef.current === null) {
        scopeBeforeMcExpandRef.current = scope;
      }
      setScopeState(canonicalizeMcDomainScope(filterScope, structure));
      return;
    }
    const prior = scopeBeforeMcExpandRef.current;
    scopeBeforeMcExpandRef.current = null;
    if (prior != null) {
      setScopeState(canonicalizeMcDomainScope(prior, structure));
    }
  };
  const [resource, setResource] = useState<ResourceFilter>('all');
  const [detailSelection, setDetailSelection] = useState<ControlBridgeDetailSelection | null>(null);
  const [entityDetailStack, setEntityDetailStack] = useState<ControlBridgeEntityDetailTarget[]>([]);
  const activeEntityDetail = entityDetailStack[entityDetailStack.length - 1] ?? null;
  const [exoFocusedItemId, setExoFocusedItemId] = useState<string | null>(null);
  const clearExoFocusedItem = useCallback(() => setExoFocusedItemId(null), []);
  const [pendingNavTick, setPendingNavTick] = useState(0);
  const [personalOverlayMode, setPersonalOverlayMode] = useState<PersonalOverlayMode>('system');
  const [meDefaultApplied, setMeDefaultApplied] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readSidebarCollapsedPreference()
  );
  const handleSidebarCollapsedChange = useCallback((collapsed: boolean) => {
    writeSidebarCollapsedPreference(collapsed);
    setSidebarCollapsed(collapsed);
  }, []);

  const companyTitle = cbDisplayCompanyTitle(structure.companyTitle || vm.companyTitle);
  const scopeLabel = resolveScopeLabel(scope, structure);
  const queueItems = useMemo(
    () => [...vm.eligibleWork, ...vm.blockedWork, ...vm.openIssues],
    [vm.eligibleWork, vm.blockedWork, vm.openIssues]
  );
  const entityDetailQueueItems = useMemo(() => queueItemsFromViewModel(vm), [vm]);
  const resourceLabel = resourceFilterLabel(resource, queueItems);
  /**
   * Scope/resource-filtered eligible rows for domain-lane scaffolding only
   * (StructureDomainNode / filterDriveLaneChildNodes). Not rendered as a work list.
   * ExoPanel membership stays company-wide; lane filter follows the active sidebar scope.
   */
  const structureLaneEligibleWork = useMemo(() => {
    const membershipScope = surface === 'exopanel' ? 'company' : scope;
    return vm.eligibleWork.filter((item) =>
      passesFilter(
        {
          domain: item.domain ?? item.filterScope,
          filterScope: item.filterScope,
          res: item.res,
          assignedResourceRef: item.assignedResourceRef,
          assignedResourceLabel: item.assignedResourceLabel,
        },
        membershipScope,
        resource,
        scopeContext
      )
    );
  }, [vm.eligibleWork, scope, resource, scopeContext, surface]);

  const resolvedResourceProfileRef = useMemo(
    () =>
      resolveUserResourceProfileRef(queueItems, {
        fullName: personalLayerUser?.label,
        username: personalLayerUser?.username,
        email: personalLayerUser?.email,
      }),
    [personalLayerUser?.email, personalLayerUser?.label, personalLayerUser?.username, queueItems]
  );

  /** Once per mount: when we can bind the user to an assignee ref, default overlay to Me. */
  useEffect(() => {
    if (meDefaultApplied) return;
    if (!resolvedResourceProfileRef) return;
    setPersonalOverlayMode('me');
    setMeDefaultApplied(true);
  }, [meDefaultApplied, resolvedResourceProfileRef]);

  const effectivePersonalLayerUser = useMemo(() => {
    if (!personalLayerUser) return undefined;
    return {
      ...personalLayerUser,
      resourceProfileRef: resolvedResourceProfileRef ?? personalLayerUser.resourceProfileRef,
      id: resolvedResourceProfileRef ?? personalLayerUser.id,
    };
  }, [personalLayerUser, resolvedResourceProfileRef]);

  const attentionRouting = useMemo(
    () =>
      deriveAttentionRouting(manifest, vm, {
        scope,
        resource,
        scopeContext,
        overlayMode: personalOverlayMode,
        user: effectivePersonalLayerUser,
      }),
    [manifest, vm, scope, resource, scopeContext, personalOverlayMode, effectivePersonalLayerUser]
  );
  const recommendedSurface = attentionRouting.recommendedSurface;

  /** First visit: open the recommended tab instead of an empty workbench (go-live first-run UX). */
  const [recommendedSurfaceApplied, setRecommendedSurfaceApplied] = useState(false);
  useEffect(() => {
    if (recommendedSurfaceApplied) return;
    if (initialSurface != null) {
      setRecommendedSurfaceApplied(true);
      return;
    }
    if (surface !== null) {
      setRecommendedSurfaceApplied(true);
      return;
    }
    if (!controlBridgeTabIsOffered(recommendedSurface)) {
      setRecommendedSurfaceApplied(true);
      return;
    }
    setSurface(recommendedSurface);
    setRecommendedSurfaceApplied(true);
  }, [recommendedSurfaceApplied, initialSurface, surface, recommendedSurface]);

  useEffect(() => {
    const onPendingNav = () => setPendingNavTick((n) => n + 1);
    window.addEventListener(CONTROL_BRIDGE_PENDING_NAV_EVENT, onPendingNav);
    return () => window.removeEventListener(CONTROL_BRIDGE_PENDING_NAV_EVENT, onPendingNav);
  }, []);

  useEffect(() => {
    const pending = peekControlBridgePendingNav();
    if (!pending) return;

    const pendingSlug = pending.workspaceSlug?.trim();
    if (pendingSlug && pendingSlug !== workspaceSlug.trim()) {
      // Handoff belongs to another CB workspace — leave storage for that mount.
      return;
    }

    const itemRef = pending.artifactItemRef?.trim();
    const catalog = queueItemsFromViewModel(vm);
    const hasQueueData = catalog.length > 0 || queueItems.length > 0;

    const applyPendingChrome = () => {
      if (
        pending.resource === 'all' ||
        pending.resource?.startsWith('person:') ||
        pending.resource?.startsWith('type:')
      ) {
        setResource(pending.resource as ResourceFilter);
      }
      const workSurfaces: ControlBridgeWorkSurfaceKey[] = [
        'exopanel',
        'drive',
        'guidance',
        'diagnose',
        'issues',
        'processflow',
        'dailybrief',
        'organizer',
        'client',
      ];
      if (pending.surface && workSurfaces.includes(pending.surface as ControlBridgeWorkSurfaceKey)) {
        if (pending.surface === 'issues') {
          setDiagnoseIssuesListOpen(true);
          setSurface('diagnose');
        } else {
          setSurface(pending.surface as ControlBridgeWorkSurfaceKey);
        }
      }
    };

    if (itemRef) {
      const item = findQueueItemById(catalog, itemRef) ?? findQueueItemById(queueItems, itemRef);
      if (!item) {
        // Wait for queues to populate before applying chrome or clearing.
        if (!hasQueueData) {
          return;
        }
        clearControlBridgePendingNav();
        return;
      }
      applyPendingChrome();
      const forceExo = pending.surface === 'exopanel';
      const route = forceExo
        ? { kind: 'exo' as const, itemId: item.itemId }
        : resolveClickTarget({
            itemId: item.itemId,
            blockerClass: item.blockerClass,
            closable: !item.requiresBridgeSession,
          });
      if (route.kind === 'exo') {
        setSurface('exopanel');
        setExoFocusedItemId(item.itemId);
        setDetailSelection({ kind: 'queue', item });
      } else {
        setDetailSelection({ kind: 'queue', item });
        expandChatPanel();
      }
      clearControlBridgePendingNav();
      return;
    }

    // Surface / resource-only handoff.
    applyPendingChrome();
    clearControlBridgePendingNav();
  }, [workspaceSlug, queueItems, vm, expandChatPanel, pendingNavTick]);

  const selectedDetailId =
    detailSelection?.kind === 'scope'
      ? detailSelection.scopeId
      : detailSelection?.kind === 'queue' || detailSelection?.kind === 'waypoint'
        ? detailSelection.item.itemId
        : undefined;

  const selectedWorkItem = useMemo(() => {
    if (detailSelection?.kind === 'queue' || detailSelection?.kind === 'waypoint') {
      return detailSelection.item;
    }
    const focusId = (exoFocusedItemId || selectedDetailId || '').trim();
    if (!focusId) return null;
    return (
      findQueueItemById(queueItems, focusId) ??
      findQueueItemById(queueItemsFromViewModel(vm), focusId) ??
      null
    );
  }, [detailSelection, exoFocusedItemId, queueItems, selectedDetailId, vm]);

  const handleSelectDetail = (selection: ControlBridgeDetailSelection) => {
    setDetailSelection(selection);
    if (selection.kind === 'queue' || selection.kind === 'waypoint') {
      expandChatPanel();
    }
  };

  /** Entity overlay — explicit Details / deep inspect only. */
  const handleOpenItemDetail = (item: ControlBridgeQueueItem, invokingLabel = 'ExoPanel') => {
    setEntityDetailStack([
      {
        kind: 'item',
        itemId: item.itemId,
        invokingLabel,
        fallbackItem: item,
      },
    ]);
  };

  /**
   * Drive / Guidance item clicks (FE-NAV / U4).
   * Ordinary + warning → Chat shelf + artifactItemRef; critical closable → Exo focus.
   */
  const handleRoutedItemClick = useCallback(
    (item: ControlBridgeQueueItem, _invokingLabel?: string) => {
      const route = resolveClickTarget({
        itemId: item.itemId,
        blockerClass: item.blockerClass,
        closable: !item.requiresBridgeSession,
      });
      if (route.kind === 'exo') {
        setSurface('exopanel');
        setExoFocusedItemId(item.itemId);
        setDetailSelection({ kind: 'queue', item });
        return;
      }
      setDetailSelection({ kind: 'queue', item });
      expandChatPanel();
    },
    [expandChatPanel]
  );

  const handleFocusExoItem = useCallback((itemId: string) => {
    setSurface('exopanel');
    setExoFocusedItemId(itemId);
  }, []);

  const handleBlockedRouteToChat = (item: ControlBridgeQueueItem, reason: string) => {
    handleRoutedItemClick(
      {
        ...item,
        blockedReason: reason || item.blockedReason,
      },
      'ExoPanel · Blocked'
    );
  };

  const handleGoDoWork = (item: ControlBridgeQueueItem, deeplink: ExoWorkDeeplink) => {
    if (deeplink.href) {
      if (/^https?:\/\//i.test(deeplink.href)) {
        window.open(deeplink.href, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(deeplink.href);
      return;
    }
    if (deeplink.surface) {
      setSurface(deeplink.surface);
      if (deeplink.scope) {
        handleScopeChange(deeplink.scope);
      }
      if (deeplink.openItemId) {
        handleOpenItemDetail(
          { ...item, itemId: deeplink.openItemId },
          'ExoPanel · Go do work'
        );
      }
    }
  };

  const handleOpenArtifactDetail = (artifactInstanceId: string, invokingLabel: string) => {
    setEntityDetailStack([
      {
        kind: 'artifact',
        artifactInstanceId,
        invokingLabel,
      },
    ]);
  };

  const handleOpenItemDetailById = (itemId: string, invokingLabel: string) => {
    setEntityDetailStack([
      {
        kind: 'item',
        itemId,
        invokingLabel,
      },
    ]);
  };

  const handleOpenNestedItemDetail = (itemId: string, invokingLabel: string) => {
    setEntityDetailStack((stack) => [
      ...stack,
      {
        kind: 'item',
        itemId,
        invokingLabel,
      },
    ]);
  };

  const handleOpenNestedArtifactDetail = (artifactInstanceId: string, invokingLabel: string) => {
    setEntityDetailStack((stack) => [
      ...stack,
      {
        kind: 'artifact',
        artifactInstanceId,
        invokingLabel,
      },
    ]);
  };

  const handleCloseEntityDetail = () => {
    setEntityDetailStack([]);
    setIssuesFocusItemId(null);
  };

  const handleBackEntityDetail = () => {
    setEntityDetailStack((stack) => (stack.length > 1 ? stack.slice(0, -1) : []));
  };

  useControlBridgeSurfaceChatContext({
    workspaceSlug,
    companyTitle,
    surface: surface ?? 'mission',
    scope,
    scopeLabel,
    resource,
    queueItems,
    artifactItemRef: selectedDetailId || selectedWorkItem?.itemId,
    resourceProfileRef: effectivePersonalLayerUser?.resourceProfileRef,
    selectedItem: selectedWorkItem,
  });

  const surfaceProps = {
    vm: { ...vm, attentionRouting, roleLens },
    manifest,
    structure,
    scope,
    resource,
    scopeContext,
    scopeLabel,
    resourceLabel,
    onNavigate: (key: string) => {
      if (key === 'mission') {
        setIssuesFocusItemId(null);
        setSurface(null);
        return;
      }
      if (key === 'issues') {
        setDiagnoseIssuesListOpen(true);
        setSurface('diagnose');
        return;
      }
      if (key !== 'diagnose') {
        setIssuesFocusItemId(null);
        setDiagnoseIssuesListOpen(false);
      }
      setSurface(key as ControlBridgeWorkSurfaceKey);
    },
    onClearResource: resource !== 'all' ? () => setResource('all') : undefined,
    onSelectDetail: handleSelectDetail,
    onOpenItemDetail: handleOpenItemDetail,
    onRoutedItemClick: handleRoutedItemClick,
    onFocusExoItem: handleFocusExoItem,
    onBlockedRouteToChat: handleBlockedRouteToChat,
    onOpenArtifactDetail: handleOpenArtifactDetail,
    onGoDoWork: handleGoDoWork,
    onScopeChange: handleScopeChange,
    selectedDetailId,
    personalLayerUser: effectivePersonalLayerUser,
    personalOverlayMode,
    onPersonalOverlayModeChange: setPersonalOverlayMode,
    workspaceSlug,
    onManifestRefresh,
    exoFocusedItemId,
    onExoFocusedItemApplied: clearExoFocusedItem,
    issuesFocusItemId,
    issuesRefreshToken,
    issuesListOpen: diagnoseIssuesListOpen,
    onOpenIssueItem: handleOpenItemDetailById,
    onDrivePeriodChange: handleDrivePeriodChange,
  };

  const renderSurface = () => {
    if (!surface) return null;
    if (isHeldControlBridgeSurface(surface)) {
      return <HeldSurfaceUnavailable surface={surface} />;
    }
    switch (surface) {
      case 'drive':
        return <DriveSurface {...surfaceProps} />;
      case 'guidance':
        return <GuidanceSurface {...surfaceProps} />;
      case 'diagnose':
        return <DiagnoseSurface {...surfaceProps} />;
      case 'issues':
        return <DiagnoseSurface {...surfaceProps} issuesListOpen />;
      case 'exopanel':
        return <ExoPanelSurface {...surfaceProps} />;
      case 'organizer':
        return <PersonalOrganizerSurface {...surfaceProps} />;
      default:
        return null;
    }
  };

  const handleTabClick = (key: ControlBridgeWorkSurfaceKey) => {
    if (!controlBridgeTabIsOffered(key)) {
      setSurface(key);
      return;
    }
    // Active-tab re-click is a no-op. Return to MC focus via compact Business /
    // Stakeholder / Customer tiles (onRequestExpand), not by toggling the surface tab.
    if (surface === key) {
      return;
    }
    if (key === 'issues') {
      setDiagnoseIssuesListOpen(true);
      setSurface('diagnose');
      return;
    }
    if (key !== 'diagnose') {
      setIssuesFocusItemId(null);
      setDiagnoseIssuesListOpen(false);
    }
    setSurface(key);
  };

  const navigateToWorkspace = (slug: string) => {
    const next = slug.trim();
    if (!next || next === workspaceSlug) return;
    const remembered = rememberPrimaryWhenLeavingWorkspace({
      fromSlug: workspaceSlug,
      toSlug: next,
      preferredPrimarySlug: preferredPrimarySlug ?? primaryReturnSlug,
    });
    setSessionPrimarySlug(remembered);
    navigate(`/render/${encodeURIComponent(next)}/control-bridge`);
  };

  const handleSelectModel = (slug: string) => {
    navigateToWorkspace(slug);
  };

  const handleOpenCrossDomainRow = (item: RenderManifestQueueItem) => {
    const targetSlug = (item.workspaceSlug ?? workspaceSlug).trim();
    if (!targetSlug) return;
    if (targetSlug === workspaceSlug) {
      setSurface('exopanel');
      return;
    }
    navigateToWorkspace(targetSlug);
  };

  const heldSurface = Boolean(surface && isHeldControlBridgeSurface(surface));
  const showHeaderModelSelect = !unifiedHub && (modelOptions ?? []).length > 1;
  const showPrimaryBack = !unifiedHub && Boolean(primaryReturnSlug);
  const pageTitle = unifiedHub ? 'Hub' : 'Control Bridge';

  return (
    <div className={styles.ControlBridgePage} data-testid="control-bridge-shell">
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderRow}>
          <h1 className={styles.pageTitle}>{pageTitle}</h1>
          <button
            type="button"
            className={styles.reportIssueHeaderBtn}
            data-testid="cb-report-issue"
            onClick={() => setReportIssueOpen(true)}
          >
            Report issue
          </button>
        </div>
        <p className={styles.pageSubtitle}>
          {unifiedHub ? (
            <>
              Personal orchestration · <strong>{unifiedHub.personalLabel}</strong>
            </>
          ) : (
            <>
              {showPrimaryBack ? (
                <button
                  type="button"
                  className={styles.headerPrimaryBackBtn}
                  data-testid="cb-primary-model-back"
                  onClick={() => navigateToWorkspace(primaryReturnSlug!)}
                >
                  ← Primary model
                </button>
              ) : null}
              {showHeaderModelSelect ? (
                <>
                  <label className={styles.headerModelLabel} htmlFor="cb-header-model-select">
                    Model
                  </label>
                  <select
                    id="cb-header-model-select"
                    className={styles.headerModelSelect}
                    value={workspaceSlug}
                    aria-label="Select company model"
                    onChange={(e) => handleSelectModel(e.target.value)}
                  >
                    {modelOptions.map((option) => {
                      const meta = modelOptionClientMeta(option);
                      return (
                        <option key={option.slug} value={option.slug}>
                          {modelOptionClientLabel(option)}
                          {meta ? ` · ${meta}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </>
              ) : (
                <span data-testid="cb-header-company">{companyTitle}</span>
              )}
            </>
          )}
          {devMock ? ' · dev mock manifest' : ''}
        </p>
      </header>

      {unifiedHub ? (
        <UnifiedHubChrome
          personalSlug={unifiedHub.personalSlug}
          personalLabel={unifiedHub.personalLabel}
          companyLinks={unifiedHub.companyLinks}
        />
      ) : null}

      {unifiedHub && crossDomainAttention ? (
        <CrossDomainAttentionStrip
          capacityHeadline={crossDomainAttention.capacityHeadline}
          profileComplete={crossDomainAttention.profileComplete}
          activeMode={crossDomainMode}
          previewItems={crossDomainPreview}
          onModeChange={setCrossDomainMode}
          onViewAllExoPanel={() => setSurface('exopanel')}
          onOpenRow={handleOpenCrossDomainRow}
        />
      ) : null}

      <div className={styles.layout}>
        <aside
          className={`${styles.sidebar} ${sidebarCollapsed ? styles.sidebarRail : ''}`}
          aria-label="Control Bridge structure"
        >
          <StructureSidebar
            structure={structure}
            companyTitle={companyTitle}
            scope={scope}
            surface={surface}
            onScopeChange={handleScopeChange}
            modelOptions={modelOptions}
            currentWorkspaceSlug={workspaceSlug}
            onSelectModel={handleSelectModel}
            sidebarCollapsed={sidebarCollapsed}
            onSidebarCollapsedChange={handleSidebarCollapsedChange}
            eligibleWorkItems={structureLaneEligibleWork}
          />
        </aside>

        <div className={styles.main}>
          <MissionControlPersonalLayer
            vm={{ ...vm, attentionRouting }}
            density={mcFocus ? 'expanded' : 'compact'}
            onRequestExpand={mcFocus ? undefined : () => {
              setIssuesFocusItemId(null);
              setSurface(null);
            }}
            structure={structure}
            onOpenArtifactDetail={handleOpenArtifactDetail}
            onOpenItemDetail={handleOpenItemDetailById}
            onNavigateToExo={handleFocusExoItem}
            onSelectDomainScope={handleMcDomainScope}
            hideEngineeringWorkspaces={modelOptions.length > 1}
            viewerResourceRef={effectivePersonalLayerUser?.resourceProfileRef}
            onOpenEngineeringWorkspace={navigateToWorkspace}
          />

          <nav className={styles.surfaceTabs} aria-label="Control Bridge surfaces">
            {CONTROL_BRIDGE_TAB_SURFACES.map((tab) => {
              const isActive = surface === tab.key;
              const isRecommended =
                tab.key === recommendedSurface &&
                (tab.key === 'exopanel' ||
                  tab.key === 'drive' ||
                  tab.key === 'guidance' ||
                  tab.key === 'diagnose');
              const owned = surfaceOwnedByLens(tab.key, roleLens);
              return (
                <button
                  key={tab.key}
                  type="button"
                  className={`${styles.surfaceTab} ${isActive ? styles.surfaceTabActive : ''} ${isRecommended ? styles.surfaceTabRecommended : ''} ${!owned ? styles.surfaceTabDimmed : ''}`}
                  onClick={() => handleTabClick(tab.key)}
                  title={
                    !owned
                      ? `${tab.blurb} · Another role owns this surface`
                      : isRecommended
                        ? `${tab.blurb} · Recommended: ${attentionRouting.reason}`
                        : tab.blurb
                  }
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={
                    !owned
                      ? `${tab.label} — another role`
                      : isRecommended
                        ? `${tab.label} — recommended: ${attentionRouting.reason}`
                        : undefined
                  }
                  data-recommended={isRecommended ? 'true' : undefined}
                  data-owned={owned ? 'true' : 'false'}
                >
                  {tab.label}
                  {isRecommended && !isActive ? (
                    <span className={styles.surfaceTabNudge} aria-hidden>
                      ·
                    </span>
                  ) : null}
                </button>
              );
            })}
          </nav>

          <div className={styles.workbenchShelfRow}>
            <div
              className={styles.surfaceWorkbench}
              data-testid="control-bridge-surface-workbench"
              data-empty={mcFocus ? 'true' : undefined}
              aria-label={mcFocus ? 'Surface panel (select a tab)' : undefined}
            >
              {!mcFocus ? (
                <>
                  {!heldSurface ? (
                  <div className={styles.resourceBar} role="toolbar" aria-label="Resource filter">
                    <span className={styles.resourceBarLabel}>Resources</span>
                    <div className={styles.resourceChipRow}>
                      {resourceOptions.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          className={`${styles.resourceChip} ${resource === opt.key ? styles.resourceChipActive : ''}`}
                          onClick={() => setResource(opt.key)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {surface === 'drive' || surface === 'guidance' || surface === 'exopanel' ? (
                      <div className={styles.resourceBarEnd}>
                        <PersonalLayerOverlayToggle
                          mode={personalOverlayMode}
                          onChange={setPersonalOverlayMode}
                        />
                      </div>
                    ) : null}
                  </div>
                  ) : null}

                  <div className={`${styles.surfaceContent} ${styles.surfaceContentActive}`}>{renderSurface()}</div>
                </>
              ) : null}
            </div>
            <ControlBridgeDetailPanel
              selection={detailSelection}
              onClose={() => setDetailSelection(null)}
            />
          </div>
        </div>
      </div>
      {activeEntityDetail ? (
        <ControlBridgeEntityDetailOverlay
          workspaceSlug={workspaceSlug}
          target={activeEntityDetail}
          queueItems={entityDetailQueueItems}
          structure={structure}
          onClose={handleCloseEntityDetail}
          onBack={entityDetailStack.length > 1 ? handleBackEntityDetail : handleCloseEntityDetail}
          onOpenItemDetail={handleOpenNestedItemDetail}
          onOpenArtifactDetail={handleOpenNestedArtifactDetail}
          onSelectStructureScope={handleScopeChange}
          onManifestRefresh={onManifestRefresh}
        />
      ) : null}
      <ReportIssueDialog
        open={reportIssueOpen}
        workspaceSlug={workspaceSlug}
        surface={surface ?? 'mission'}
        scopeId={scope}
        onClose={() => setReportIssueOpen(false)}
        onSubmitted={async (itemId) => {
          setIssuesFocusItemId(itemId);
          setIssuesRefreshToken((n) => n + 1);
          setDiagnoseIssuesListOpen(true);
          setSurface('diagnose');
          handleOpenItemDetailById(itemId, 'Issues');
          await onManifestRefresh?.();
        }}
      />
    </div>
  );
};

export default ControlBridgeShell;
